import { Link } from "react-router-dom";
import { ArrowUpRight, Check, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Reveal, MaskedLines } from "@/components/Reveal";

const MEETING_IMG =
  "https://images.pexels.com/photos/8428082/pexels-photo-8428082.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function Conformite() {
  const { t } = useTranslation("conformite");
  const kycCards = t("kyc.cards", { returnObjects: true });
  const examine = t("dd.examine", { returnObjects: true });
  const immoBullets = t("immo.bullets", { returnObjects: true });

  return (
    <div data-testid="conformite-page" className="pt-32 lg:pt-40">
      <header className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-12">
        <p className="overline-label mb-8 flex items-center gap-3">
          <span className="inline-block h-px w-10 bg-brass/60" />
          {t("hero.overline")}
        </p>
        <h1 className="max-w-3xl font-display text-4xl font-light leading-[1.08] tracking-tight text-chalk sm:text-5xl lg:text-6xl">
          <MaskedLines
            lines={[
              t("hero.titleA"),
              <span key="b" className="font-serif italic text-brass">{t("hero.titleB")}</span>,
            ]}
          />
        </h1>
        <p className="mt-8 max-w-2xl text-base leading-relaxed text-mist/85">{t("hero.lead")}</p>
      </header>

      <section className="mx-auto mt-16 max-w-7xl px-6 sm:px-8 lg:px-12 lg:mt-24">
        <Reveal>
          <p className="overline-label">{t("kyc.overline")}</p>
          <h2 className="mt-4 font-display text-2xl font-normal tracking-tight text-chalk sm:text-3xl">
            {t("kyc.titleA")}
            <span className="font-serif italic text-brass">{t("kyc.titleB")}</span>
          </h2>
          <p className="mt-6 max-w-2xl text-sm leading-relaxed text-mist/85">{t("kyc.lead")}</p>
        </Reveal>
        <div className="mt-12 grid gap-px bg-white/[0.06] lg:grid-cols-3">
          {kycCards.map((c, i) => (
            <Reveal key={c.title} delay={i * 0.08} className="bg-ink">
              <div data-testid={`kyc-card-${i + 1}`} className="h-full p-8 transition-colors duration-300 hover:bg-elevated lg:p-10">
                <ShieldCheck size={20} className="text-brass" />
                <h3 className="mt-5 font-display text-lg text-chalk">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate2">{c.description}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-32">
          <Reveal className="lg:col-span-7">
            <p className="overline-label">{t("dd.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("dd.titleA")}
              <span className="font-serif italic text-brass">{t("dd.titleB")}</span>
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-relaxed text-mist/85">
              <p>{t("dd.para1")}</p>
              <p className="text-slate2">{t("dd.para2")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="border border-white/[0.08] bg-ink p-8 lg:p-10">
              <p className="overline-label mb-6">{t("dd.examineLabel")}</p>
              <ul className="space-y-3">
                {examine.map((d) => (
                  <li key={d} className="flex items-start gap-3 border-b border-white/[0.06] pb-3 text-sm text-mist/80 last:border-b-0">
                    <Check size={15} className="mt-0.5 shrink-0 text-brass" />
                    {d}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-32">
        <div className="grid items-center gap-12 lg:grid-cols-12">
          <Reveal className="lg:col-span-5">
            <div className="overflow-hidden border border-white/10">
              <img
                src={MEETING_IMG}
                alt={t("immo.imgAlt")}
                className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105 lg:h-[420px]"
              />
            </div>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-7">
            <p className="overline-label">{t("immo.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("immo.titleA")}
              <span className="font-serif italic text-brass">{t("immo.titleB")}</span>
            </h2>
            <div className="mt-6 space-y-5 text-sm leading-relaxed text-mist/85">
              <p>{t("immo.para1")}</p>
              <p className="text-slate2">{t("immo.para2")}</p>
            </div>
            <ul className="mt-8 grid gap-3 border-t border-white/[0.08] pt-8 sm:grid-cols-2">
              {immoBullets.map((d) => (
                <li key={d} className="flex items-start gap-3 text-sm text-mist/80">
                  <Check size={15} className="mt-0.5 shrink-0 text-brass" />
                  {d}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/30">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 py-24 sm:px-8 lg:grid-cols-12 lg:px-12 lg:py-32">
          <Reveal className="lg:col-span-7">
            <p className="overline-label">{t("transparency.overline")}</p>
            <h2 className="mt-4 font-display text-2xl font-normal leading-[1.18] tracking-tight text-chalk sm:text-3xl">
              {t("transparency.titleA")}
              <span className="font-serif italic text-brass">{t("transparency.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-sm leading-relaxed text-mist/85">{t("transparency.lead")}</p>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5">
            <div className="flex h-full flex-col justify-between border border-brass/25 bg-elevated/40 p-8 lg:p-10">
              <div>
                <p className="overline-label">{t("transparency.commissionLabel")}</p>
                <p className="mt-6 font-display text-5xl text-chalk" data-testid="commission-range">
                  4 %<span className="mx-2 text-brass">—</span>12 %
                </p>
              </div>
              <p className="mt-8 text-xs leading-relaxed text-slate2">{t("transparency.commissionText")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-white/[0.06] bg-surface/40">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12 lg:py-28">
          <Reveal className="max-w-3xl">
            <h2 className="font-display text-3xl font-light leading-[1.15] tracking-tight text-chalk sm:text-4xl">
              {t("final.titleA")}
              <span className="font-serif italic text-brass">{t("final.titleB")}</span>
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-mist/85">{t("final.lead")}</p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/contact" data-testid="conformite-cta-appointment" className="btn-brass">
                {t("final.btnPrimary")} <ArrowUpRight size={15} />
              </Link>
              <Link to="/services" data-testid="conformite-cta-services" className="btn-ghost">
                {t("final.btnSecondary")}
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
